/**
 * Un presupuesto de proveedor: anotarlo, aceptarlo y seguir la obra contra él
 * — `OD-0055` (ACCEPTED 2026-10-22), plan Business para escribir.
 *
 * Un presupuesto es una ANOTACIÓN para seguir un trabajo (`D-01`): no es un
 * documento fiscal, no va al libro de facturas recibidas ni a la exportación
 * para la gestoría. Se corrige y se cancela con motivo, pero no se borra.
 *
 * ## Lo que esta pantalla NO decide
 *
 * - **Ninguna cuenta de dinero** (`URS §129`): los totales mientras se escribe
 *   los da `POST /supplier-quotes/calculate`, y lo facturado, lo pagado, lo
 *   que queda y el estado llegan de la API (`D-05`).
 * - **Ni qué acción cabe**: aceptar, dar por terminado, deshacerlo, cancelar o
 *   cambiar de proveedor los dice `actions`, que la API vuelve a comprobar.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  apiCompras,
  apiPresupuestosProveedor,
  ESTADO_VALIDACION,
  type AccionPresupuestoProveedor,
  type AltaPresupuestoProveedor,
  type AvisoCompra,
  type CalculoCompra,
  type DatosPresupuestoProveedor,
  type PresupuestoProveedor,
  type ProveedorDeLista,
} from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible } from "../ui/formato";
import { Aviso, Boton, Campo, FilaPulsable, Selector, Tarjeta } from "../ui/primitivas";
import {
  ACEPTA_ADJUNTO,
  AdjuntoDocumento,
  CifrasCalculadas,
  EditorDeLineas,
  EditorDeRetencion,
  LINEA_VACIA,
  lineasDelFormulario,
  lineasParaLaApi,
  mensajeDeRechazo,
  NUEVO,
  SIN_RETENCION,
  textoDeAviso,
  type DesdePresupuesto,
  type LineaForm,
  type Mensaje,
} from "./PantallaCompra";
import { InsigniaPago, InsigniaPresupuesto } from "./PantallaCompras";
import { diaLegible } from "./PantallaOportunidades";

type Form = {
  readonly proveedorId: string;
  readonly nif: string;
  readonly nombre: string;
  readonly numero: string;
  readonly fecha: string;
  readonly validoHasta: string;
  readonly referencia: string;
  readonly lineas: readonly LineaForm[];
  readonly retencionTipo: string;
  readonly retencion: string;
};

function formVacio(): Form {
  return { proveedorId: "", nif: "", nombre: "", numero: "", fecha: "", validoHasta: "", referencia: "", lineas: [LINEA_VACIA], retencionTipo: SIN_RETENCION, retencion: "" };
}

function formDePresupuesto(q: PresupuestoProveedor): Form {
  return {
    proveedorId: q.supplier.id,
    nif: q.supplier.taxId,
    nombre: q.supplier.name,
    numero: q.supplierQuoteNumber ?? "",
    fecha: q.issueDate,
    validoHasta: q.validUntil ?? "",
    referencia: q.reference ?? "",
    lineas: lineasDelFormulario(q.lines),
    retencionTipo: q.withholdingRate ?? SIN_RETENCION,
    retencion: q.withholdingRate === null ? "" : q.withholding,
  };
}

const nulo = (v: string): string | null => (v.trim() === "" ? null : v.trim());

function datosDe(f: Form): DatosPresupuestoProveedor {
  return {
    supplierQuoteNumber: nulo(f.numero),
    issueDate: f.fecha,
    validUntil: nulo(f.validoHasta),
    reference: nulo(f.referencia),
    lines: lineasParaLaApi(f.lineas),
    withholdingRate: f.retencionTipo === SIN_RETENCION ? null : f.retencionTipo,
    withholding: f.retencionTipo === SIN_RETENCION ? null : nulo(f.retencion),
  };
}

function altaDe(f: Form): AltaPresupuestoProveedor {
  const base = datosDe(f);
  return f.proveedorId === NUEVO || f.proveedorId === "" ? { ...base, supplier: { taxId: f.nif.trim(), name: f.nombre.trim() } } : { ...base, supplierId: f.proveedorId };
}

function FormularioPresupuesto({
  presupuesto,
  hayConexion,
  onGuardado,
  onCancelar,
  onSesionCaducada,
}: {
  /** `null` para anotar uno nuevo; el presupuesto, para corregirlo. */
  presupuesto: PresupuestoProveedor | null;
  hayConexion: boolean;
  onGuardado: (q: PresupuestoProveedor, avisos: readonly AvisoCompra[], mensaje: Mensaje | null) => void;
  onCancelar: () => void;
  onSesionCaducada: () => void;
}) {
  const alta = presupuesto === null;
  // D-02: el proveedor cambia solo sin facturas ni pagos enlazados; lo dice la API.
  const cambiaProveedor = alta || presupuesto.actions.changeSupplier;
  const [form, setForm] = useState<Form>(() => (presupuesto === null ? formVacio() : formDePresupuesto(presupuesto)));
  const [proveedores, setProveedores] = useState<readonly ProveedorDeLista[]>([]);
  const [calculo, setCalculo] = useState<CalculoCompra | null>(null);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [fichero, setFichero] = useState<File | null>(null);
  // Un alta reintentada con mala conexión no anota dos presupuestos (`Idempotency-Key`).
  const [clave] = useState(() => crypto.randomUUID());
  const ultimoCalculo = useRef(0);

  const cambiar = (parcial: Partial<Form>): void => setForm((f) => ({ ...f, ...parcial }));

  useEffect(() => {
    if (!cambiaProveedor) return;
    void apiCompras.listarProveedores("ACTIVE", "").then((r) => {
      if (r.ok) setProveedores(r.datos.proveedores);
      else if (r.estado === 401) onSesionCaducada();
    });
  }, [cambiaProveedor, onSesionCaducada]);

  // Los totales y los avisos, de la API, un momento después de dejar de escribir.
  const cuerpo = JSON.stringify(alta ? altaDe(form) : datosDe(form));
  useEffect(() => {
    const n = ++ultimoCalculo.current;
    const h = setTimeout(() => {
      void apiPresupuestosProveedor.calcular(JSON.parse(cuerpo) as Partial<AltaPresupuestoProveedor>).then((r) => {
        if (n !== ultimoCalculo.current) return;
        if (r.ok) setCalculo(r.datos);
        else if (r.estado === 401) onSesionCaducada();
      });
    }, 400);
    return () => clearTimeout(h);
  }, [cuerpo, onSesionCaducada]);

  async function guardar(): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r =
      presupuesto === null
        ? await apiPresupuestosProveedor.anotar(altaDe(form), clave)
        : await apiPresupuestosProveedor.corregir(presupuesto.id, { ...datosDe(form), supplierId: form.proveedorId }, presupuesto.updatedAt);
    if (!r.ok) {
      setOcupado(false);
      if (r.estado === 401) return onSesionCaducada();
      if (r.estado === ESTADO_VALIDACION && r.error !== null) {
        setErrores(r.error.campos);
        return setMensaje({ tono: "aviso", texto: t.compras.revisa });
      }
      return setMensaje(mensajeDeRechazo(r));
    }
    // El adjunto va después: si no se sube, el presupuesto ya está anotado y se dice.
    let tras: Mensaje | null = null;
    if (fichero !== null) {
      const s = await apiPresupuestosProveedor.subirAdjunto(r.datos.presupuesto.id, fichero);
      if (!s.ok) tras = { tono: "aviso", texto: `${t.compras.presupuestos.adjuntoNoSubido} ${mensajeDeRechazo(s).texto}` };
    }
    setOcupado(false);
    const otro = fichero !== null && tras === null ? await apiPresupuestosProveedor.obtener(r.datos.presupuesto.id) : null;
    onGuardado(otro?.ok === true ? otro.datos : r.datos.presupuesto, r.datos.avisos, tras);
  }

  const errorDe = (k: string): string | null => {
    const c = errores[k];
    return c === undefined ? null : (t.compras.presupuestos.errores[c] ?? t.compras.errores[c] ?? t.comun.errorGenerico);
  };
  const totales = calculo !== null && calculo.ok ? calculo.totales : null;
  const avisos = calculo !== null && calculo.ok ? calculo.avisos : [];
  // Al corregir no se da de alta un proveedor: se elige entre los que hay, y el actual va primero.
  const opcionesProveedor = proveedores.map((p) => ({ valor: p.id, texto: `${p.name} · ${p.taxId}` }));

  return (
    <Tarjeta titulo={presupuesto === null ? t.compras.presupuestos.anotarTitulo : t.compras.presupuestos.corregirTitulo(presupuesto.quoteNumber)}>
      {!hayConexion ? <Aviso tono="aviso">{t.compras.sinConexion}</Aviso> : null}
      <p className="ayuda-neutra">{t.compras.presupuestos.noFiscal}</p>

      {alta ? (
        <>
          <Selector
            id="presupuesto-proveedor-proveedor"
            etiqueta={t.compras.proveedor}
            valor={form.proveedorId === "" ? NUEVO : form.proveedorId}
            opciones={[{ valor: NUEVO, texto: t.compras.proveedorNuevo }, ...opcionesProveedor]}
            onChange={(v) => cambiar({ proveedorId: v })}
            error={errorDe("supplierId")}
          />
          {form.proveedorId === "" || form.proveedorId === NUEVO ? (
            <>
              <Campo id="presupuesto-proveedor-nif" etiqueta={t.compras.nif} valor={form.nif} onChange={(v) => cambiar({ nif: v })} error={errorDe("taxId")} ayuda={t.compras.ayudaNif} />
              <Campo id="presupuesto-proveedor-nombre" etiqueta={t.compras.nombre} valor={form.nombre} onChange={(v) => cambiar({ nombre: v })} error={errorDe("name")} />
            </>
          ) : null}
        </>
      ) : cambiaProveedor ? (
        <Selector
          id="presupuesto-proveedor-proveedor"
          etiqueta={t.compras.proveedor}
          valor={form.proveedorId}
          opciones={
            opcionesProveedor.some((o) => o.valor === presupuesto.supplier.id)
              ? opcionesProveedor
              : [{ valor: presupuesto.supplier.id, texto: `${presupuesto.supplier.name} · ${presupuesto.supplier.taxId}` }, ...opcionesProveedor]
          }
          onChange={(v) => cambiar({ proveedorId: v })}
          error={errorDe("supplierId")}
        />
      ) : (
        <>
          <p>
            {presupuesto.supplier.name} · {presupuesto.supplier.taxId}
          </p>
          <p className="ayuda-neutra">{t.compras.presupuestos.proveedorNoCambia}</p>
        </>
      )}

      <Campo
        id="presupuesto-proveedor-numero"
        etiqueta={t.compras.presupuestos.numero}
        requerido={false}
        valor={form.numero}
        onChange={(v) => cambiar({ numero: v })}
        ayuda={t.compras.presupuestos.ayudaNumero}
        error={errorDe("supplierQuoteNumber")}
      />
      <Campo id="presupuesto-proveedor-fecha" etiqueta={t.compras.presupuestos.fecha} tipo="date" valor={form.fecha} onChange={(v) => cambiar({ fecha: v })} error={errorDe("issueDate")} />
      <Campo
        id="presupuesto-proveedor-valido"
        etiqueta={t.compras.presupuestos.validoHasta}
        tipo="date"
        requerido={false}
        valor={form.validoHasta}
        onChange={(v) => cambiar({ validoHasta: v })}
        ayuda={t.compras.presupuestos.ayudaValido}
        error={errorDe("validUntil")}
      />
      <Campo
        id="presupuesto-proveedor-referencia"
        etiqueta={t.compras.referencia}
        requerido={false}
        valor={form.referencia}
        onChange={(v) => cambiar({ referencia: v })}
        ayuda={t.compras.ayudaReferencia}
        error={errorDe("reference")}
      />

      <EditorDeLineas prefijo="presupuesto-proveedor" ayuda={t.compras.presupuestos.ayudaLineas} lineas={form.lineas} onChange={(lineas) => cambiar({ lineas })} errorDe={errorDe} />
      <EditorDeRetencion
        prefijo="presupuesto-proveedor"
        tipo={form.retencionTipo}
        importe={form.retencion}
        onTipo={(v) => cambiar({ retencionTipo: v })}
        onImporte={(v) => cambiar({ retencion: v })}
        errorDe={errorDe}
      />

      {alta ? (
        <section className="grupo-campos" aria-label={t.compras.presupuestos.adjuntoTitulo}>
          <h3 className="subtitulo">{t.compras.presupuestos.adjuntoTitulo}</h3>
          <input
            id="presupuesto-proveedor-adjunto-alta"
            className="entrada-fichero"
            type="file"
            accept={ACEPTA_ADJUNTO}
            disabled={ocupado}
            onChange={(e) => setFichero(e.target.files?.[0] ?? null)}
          />
          <div className="acciones-en-linea">
            <label className="boton boton-secundario enlace-boton" htmlFor="presupuesto-proveedor-adjunto-alta">
              {fichero === null ? t.compras.adjunto.elegir : t.compras.adjunto.cambiar}
            </label>
            {fichero !== null ? (
              <Boton variante="terciario" onClick={() => setFichero(null)}>
                {t.compras.adjunto.noAdjuntar}
              </Boton>
            ) : null}
          </div>
          {fichero !== null ? <p>{t.compras.adjunto.elegido(fichero.name)}</p> : <p className="ayuda-neutra">{t.compras.presupuestos.adjuntoAyuda}</p>}
        </section>
      ) : null}

      {/* Lo que sale de la API: totales y avisos. La web no suma. */}
      {totales !== null ? <CifrasCalculadas totales={totales} conRetencion={form.retencionTipo !== SIN_RETENCION} etiquetaTotal={t.compras.presupuestos.total} /> : null}
      {avisos.map((a, i) => (
        <Aviso key={`${a.code}-${i}`} tono="info">
          {textoDeAviso(a)}
        </Aviso>
      ))}

      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      <div className="acciones">
        <Boton onClick={() => void guardar()} deshabilitado={ocupado || !hayConexion}>
          {ocupado ? t.compras.guardando : alta ? t.compras.presupuestos.guardar : t.compras.guardarCorreccion}
        </Boton>
        <Boton variante="secundario" onClick={onCancelar}>
          {t.compras.cancelar}
        </Boton>
      </div>
    </Tarjeta>
  );
}

// ------------------------------------------------------------------ ficha

/** Lo que dice cada acción cuando sale bien. */
const HECHO: Readonly<Record<AccionPresupuestoProveedor, string>> = {
  accept: t.compras.presupuestos.aceptado,
  close: t.compras.presupuestos.cerrado,
  reopen: t.compras.presupuestos.reabierto,
  cancel: t.compras.presupuestos.canceladoOk,
};

function FichaPresupuesto({
  presupuesto: q,
  hayConexion,
  mensajeInicial,
  onPresupuesto,
  onCorregir,
  onAbrirCompra,
  onAbrirProveedor,
  onAnotarFactura,
  onVerEnRecibidas,
  onSesionCaducada,
}: {
  presupuesto: PresupuestoProveedor;
  hayConexion: boolean;
  mensajeInicial: Mensaje | null;
  onPresupuesto: (q: PresupuestoProveedor) => void;
  onCorregir: () => void;
  onAbrirCompra: (id: string) => void;
  onAbrirProveedor: (id: string) => void;
  onAnotarFactura: () => void;
  onVerEnRecibidas: () => void;
  onSesionCaducada: () => void;
}) {
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<Mensaje | null>(mensajeInicial);
  const [ocupado, setOcupado] = useState(false);

  async function hacer(accion: AccionPresupuestoProveedor): Promise<void> {
    setOcupado(true);
    setError(null);
    setMensaje(null);
    const r = await apiPresupuestosProveedor.accion(q.id, accion, accion === "cancel" ? motivo : null);
    setOcupado(false);
    if (r.ok) {
      setCancelando(false);
      setMensaje({ tono: "exito", texto: HECHO[accion] });
      return onPresupuesto(r.datos);
    }
    if (r.estado === 401) return onSesionCaducada();
    if (accion === "cancel" && r.estado === ESTADO_VALIDACION && r.error !== null) {
      return setError(t.compras.errores[r.error.campos.reason ?? "REQUIRED"] ?? t.comun.errorGenerico);
    }
    setMensaje(mensajeDeRechazo(r));
  }

  const a = q.actions;
  const escribe = q.writable;
  return (
    <Tarjeta titulo={t.compras.presupuestos.fichaTitulo(q.quoteNumber)}>
      <p className="fila-titulo">
        <button type="button" className="enlace-en-texto" onClick={() => onAbrirProveedor(q.supplier.id)}>
          {q.supplier.name}
        </button>
        {` · ${q.supplier.taxId}`}
      </p>
      <p>
        <InsigniaPresupuesto estado={q.state} />
      </p>
      {q.state === "CANCELLED" ? <Aviso tono="info">{t.compras.presupuestos.cancelado(q.cancelReason ?? "")}</Aviso> : null}
      {q.closed && q.state === "COMPLETED" ? <p className="ayuda-neutra">{t.compras.presupuestos.cerradoAyuda}</p> : null}
      {q.overInvoiced ? <Aviso tono="aviso">{t.compras.presupuestos.facturadoDeMas}</Aviso> : null}
      {q.lateUnbilledPayments > 0 ? <Aviso tono="aviso">{t.compras.presupuestos.pagosTarde(q.lateUnbilledPayments)}</Aviso> : null}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}

      <dl className="datos">
        {q.supplierQuoteNumber !== null ? (
          <div>
            <dt>{t.compras.presupuestos.suNumeroEtiqueta}</dt>
            <dd>{q.supplierQuoteNumber}</dd>
          </div>
        ) : null}
        <div>
          <dt>{t.compras.presupuestos.fecha}</dt>
          <dd>{diaLegible(q.issueDate)}</dd>
        </div>
        {q.validUntil !== null ? (
          <div>
            <dt>{t.compras.presupuestos.validez}</dt>
            <dd>{diaLegible(q.validUntil)}</dd>
          </div>
        ) : null}
        {q.reference !== null ? (
          <div>
            <dt>{t.compras.datos.referencia}</dt>
            <dd>{q.reference}</dd>
          </div>
        ) : null}
      </dl>

      {/* D-05: las cifras de la obra, todas de la API. */}
      <dl className="cifras">
        <div>
          <dt>{t.compras.presupuestos.total}</dt>
          <dd>{importeLegible(q.total)} €</dd>
        </div>
        {q.withholdingRate !== null ? (
          <div>
            <dt>{t.compras.retencionDe(t.compras.porcentaje(q.withholdingRate))}</dt>
            <dd>{importeLegible(q.withholding)} €</dd>
          </div>
        ) : null}
        <div>
          <dt>{t.compras.presupuestos.aPagar}</dt>
          <dd>{importeLegible(q.payable)} €</dd>
        </div>
        <div>
          <dt>{t.compras.presupuestos.facturado}</dt>
          <dd>{importeLegible(q.invoiced)} €</dd>
        </div>
        <div>
          <dt>{t.compras.presupuestos.porFacturar}</dt>
          <dd>{importeLegible(q.toInvoice)} €</dd>
        </div>
        <div>
          <dt>{t.compras.presupuestos.pagado}</dt>
          <dd>{importeLegible(q.paid)} €</dd>
        </div>
        <div>
          <dt>{t.compras.presupuestos.porPagar}</dt>
          <dd>{importeLegible(q.toPay)} €</dd>
        </div>
      </dl>

      <h3>{t.compras.lineas}</h3>
      <ul className="lista-filas">
        {q.lines.map((l, i) => (
          <li key={i} className="fila">
            <span className="fila-texto">
              <span className="fila-titulo">{l.vatRate !== null ? t.compras.porcentaje(l.vatRate) : t.compras.tratamientos[l.vatTreatment]}</span>
              <span className="ayuda-neutra">{t.compras.lineaResumen(importeLegible(l.base), importeLegible(l.vat))}</span>
            </span>
          </li>
        ))}
      </ul>

      {escribe && (a.accept || a.close || a.reopen || a.addPurchase) ? (
        <div className="acciones">
          {a.accept ? (
            <Boton onClick={() => void hacer("accept")} deshabilitado={ocupado || !hayConexion}>
              {t.compras.presupuestos.aceptar}
            </Boton>
          ) : null}
          {a.addPurchase ? (
            <Boton onClick={onAnotarFactura} deshabilitado={!hayConexion}>
              {t.compras.presupuestos.anotarFacturaDe}
            </Boton>
          ) : null}
          {a.close ? (
            <Boton variante="secundario" onClick={() => void hacer("close")} deshabilitado={ocupado || !hayConexion}>
              {t.compras.presupuestos.cerrar}
            </Boton>
          ) : null}
          {a.reopen ? (
            <Boton variante="secundario" onClick={() => void hacer("reopen")} deshabilitado={ocupado || !hayConexion}>
              {t.compras.presupuestos.reabrir}
            </Boton>
          ) : null}
        </div>
      ) : null}
      {escribe && a.close ? <p className="ayuda-neutra">{t.compras.presupuestos.ayudaCerrar}</p> : null}

      <section aria-label={t.compras.presupuestos.facturas}>
        <h3>{t.compras.presupuestos.facturas}</h3>
        {q.purchases.length === 0 ? <p className="ayuda-neutra">{t.compras.presupuestos.sinFacturas}</p> : null}
        {q.purchases.length > 0 ? (
          <>
            <ul className="lista-filas">
              {q.purchases.map((c) => (
                <FilaPulsable key={c.id} onClick={() => onAbrirCompra(c.id)}>
                  <span className={c.status === "VOIDED" ? "fila-titulo cobro-anulado" : "fila-titulo"}>
                    {c.registryNumber} · {t.compras.suFactura(c.supplierInvoiceNumber)}
                  </span>
                  <span className="ayuda-neutra">
                    {diaLegible(c.issueDate)} · {t.compras.tipos[c.kind]}
                    {c.status === "VOIDED" ? ` · ${t.compras.anulada}` : ""}
                  </span>
                  {c.status === "REGISTERED" ? (
                    <span className="ayuda-neutra">
                      <InsigniaPago estado={c.paymentStatus} /> · {t.compras.deTotal(importeLegible(c.pending), importeLegible(c.payable))}
                    </span>
                  ) : null}
                </FilaPulsable>
              ))}
            </ul>
            <Boton variante="terciario" onClick={onVerEnRecibidas}>
              {t.compras.presupuestos.verEnRecibidas}
            </Boton>
          </>
        ) : null}
      </section>

      {q.unbilledPayments.length > 0 ? (
        <section aria-label={t.compras.presupuestos.pagosSinFactura}>
          <h3>{t.compras.presupuestos.pagosSinFactura}</h3>
          {q.unbilledPayments.map((x) => (
            <Aviso key={x.id} tono={x.late ? "aviso" : "info"}>
              {t.compras.pagoSinFacturaLinea(importeLegible(x.amount), diaLegible(x.paidOn))} {t.compras.faltaFactura}
              {x.late ? ` ${t.compras.proveedorTarde}` : ""}
            </Aviso>
          ))}
        </section>
      ) : null}

      {q.payments.length > 0 ? (
        <section aria-label={t.compras.presupuestos.pagos}>
          <h3>{t.compras.presupuestos.pagos}</h3>
          <ul className="lista-filas">
            {q.payments.map((x) => (
              <li key={x.id} className="fila">
                <span className="fila-texto">
                  <span className={x.status === "VOIDED" ? "fila-titulo cobro-anulado" : "fila-titulo"}>{importeLegible(x.amount)} €</span>
                  <span className="ayuda-neutra">
                    {diaLegible(x.paidOn)} · {t.cobros.metodos[x.method]} · {x.purchaseId === null ? t.compras.sinFacturaCorto : t.compras.conFactura}
                  </span>
                  {x.status === "VOIDED" ? <span className="ayuda-neutra">{t.compras.anuladoPor(x.voidReason ?? "")}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <AdjuntoDocumento
        prefijo="presupuesto-proveedor"
        titulo={t.compras.presupuestos.adjuntoTitulo}
        adjunto={q.attachment}
        puede={escribe}
        hayConexion={hayConexion}
        textoQuitado={t.compras.presupuestos.adjuntoQuitado}
        subir={(f) => apiPresupuestosProveedor.subirAdjunto(q.id, f)}
        quitar={() => apiPresupuestosProveedor.quitarAdjunto(q.id)}
        descargar={() => apiPresupuestosProveedor.descargarAdjunto(q.id)}
        recargar={async () => {
          const otro = await apiPresupuestosProveedor.obtener(q.id);
          if (otro.ok) onPresupuesto(otro.datos);
        }}
        onSesionCaducada={onSesionCaducada}
      />

      {escribe && q.state !== "CANCELLED" ? (
        <div className="acciones">
          <Boton variante="secundario" onClick={onCorregir} deshabilitado={!hayConexion}>
            {t.compras.corregir}
          </Boton>
          {!cancelando ? (
            <Boton variante="peligro" onClick={() => setCancelando(true)} deshabilitado={!hayConexion}>
              {t.compras.presupuestos.cancelar}
            </Boton>
          ) : null}
        </div>
      ) : null}
      {cancelando ? (
        <div>
          <p className="ayuda-neutra">{a.cancel ? t.compras.presupuestos.ayudaCancelar : t.compras.presupuestos.noCancelable}</p>
          {a.cancel ? (
            <>
              <Campo id="presupuesto-proveedor-motivo" etiqueta={t.compras.presupuestos.motivoCancelar} valor={motivo} onChange={setMotivo} error={error} />
              <div className="acciones">
                <Boton variante="peligro" onClick={() => void hacer("cancel")} deshabilitado={ocupado || !hayConexion}>
                  {t.compras.presupuestos.confirmarCancelar}
                </Boton>
                <Boton variante="secundario" onClick={() => setCancelando(false)}>
                  {t.compras.presupuestos.volver}
                </Boton>
              </div>
            </>
          ) : (
            <Boton variante="secundario" onClick={() => setCancelando(false)}>
              {t.compras.presupuestos.volver}
            </Boton>
          )}
        </div>
      ) : null}
    </Tarjeta>
  );
}

// --------------------------------------------------------------- pantalla

type Vista =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "no-encontrado" }
  | { readonly tipo: "ficha"; readonly presupuesto: PresupuestoProveedor; readonly mensaje: Mensaje | null }
  | { readonly tipo: "corregir"; readonly presupuesto: PresupuestoProveedor };

export function PantallaPresupuestoProveedor({
  id,
  hayConexion,
  onVolver,
  onAbrirCompra,
  onAbrirProveedor,
  onAnotarFactura,
  onVerEnRecibidas,
  onSesionCaducada,
}: {
  id: string | null;
  hayConexion: boolean;
  onVolver: () => void;
  onAbrirCompra: (id: string) => void;
  onAbrirProveedor: (id: string) => void;
  /** La factura nace con el proveedor, el presupuesto y su referencia (`D-04`, `D-08`). */
  onAnotarFactura: (proveedorId: string, desde: DesdePresupuesto) => void;
  onVerEnRecibidas: (presupuesto: { readonly id: string; readonly numero: string }) => void;
  onSesionCaducada: () => void;
}) {
  // Tras anotar, la misma pantalla enseña la ficha de lo anotado, con su aviso.
  const [actual, setActual] = useState<string | null>(id);
  const [vista, setVista] = useState<Vista>({ tipo: "cargando" });
  const yaCargado = useRef<string | null>(null);

  const cargar = useCallback(
    async (presupuestoId: string): Promise<void> => {
      setVista({ tipo: "cargando" });
      const r = await apiPresupuestosProveedor.obtener(presupuestoId);
      if (r.ok) return setVista({ tipo: "ficha", presupuesto: r.datos, mensaje: null });
      if (r.estado === 401) return onSesionCaducada();
      setVista({ tipo: r.estado === 404 ? "no-encontrado" : "error" });
    },
    [onSesionCaducada],
  );

  useEffect(() => {
    if (actual === null || yaCargado.current === actual) return;
    void cargar(actual);
  }, [actual, cargar]);

  const guardado = (q: PresupuestoProveedor, avisos: readonly AvisoCompra[], tras: Mensaje | null, alta: boolean): void => {
    const primero = alta ? t.compras.presupuestos.guardado(q.quoteNumber) : t.compras.presupuestos.corregido;
    const texto = [primero, ...avisos.map(textoDeAviso)].join(" ");
    setVista({ tipo: "ficha", presupuesto: q, mensaje: tras ?? { tono: avisos.length > 0 ? "info" : "exito", texto } });
    yaCargado.current = q.id;
    setActual(q.id);
  };

  const volver = (
    <div className="barra-acciones">
      <Boton variante="secundario" onClick={onVolver}>
        {t.compras.volver}
      </Boton>
    </div>
  );

  if (actual === null) {
    return (
      <>
        {volver}
        <FormularioPresupuesto
          presupuesto={null}
          hayConexion={hayConexion}
          onGuardado={(q, avisos, tras) => guardado(q, avisos, tras, true)}
          onCancelar={onVolver}
          onSesionCaducada={onSesionCaducada}
        />
      </>
    );
  }

  return (
    <>
      {volver}
      {vista.tipo === "cargando" ? <p>{t.compras.cargando}</p> : null}
      {vista.tipo === "no-encontrado" ? <Aviso tono="aviso">{t.compras.presupuestos.noEncontrado}</Aviso> : null}
      {vista.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.compras.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar(actual)}>
            {t.compras.reintentar}
          </Boton>
        </>
      ) : null}
      {vista.tipo === "ficha" ? (
        <FichaPresupuesto
          key={vista.presupuesto.id}
          presupuesto={vista.presupuesto}
          hayConexion={hayConexion}
          mensajeInicial={vista.mensaje}
          onPresupuesto={(q) => setVista({ tipo: "ficha", presupuesto: q, mensaje: null })}
          onCorregir={() => setVista({ tipo: "corregir", presupuesto: vista.presupuesto })}
          onAbrirCompra={onAbrirCompra}
          onAbrirProveedor={onAbrirProveedor}
          onAnotarFactura={() => onAnotarFactura(vista.presupuesto.supplier.id, { id: vista.presupuesto.id, referencia: vista.presupuesto.reference })}
          onVerEnRecibidas={() => onVerEnRecibidas({ id: vista.presupuesto.id, numero: vista.presupuesto.quoteNumber })}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}
      {vista.tipo === "corregir" ? (
        <FormularioPresupuesto
          presupuesto={vista.presupuesto}
          hayConexion={hayConexion}
          onGuardado={(q, avisos, tras) => guardado(q, avisos, tras, false)}
          onCancelar={() => setVista({ tipo: "ficha", presupuesto: vista.presupuesto, mensaje: null })}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}
    </>
  );
}
